import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';

const ACTION_TONE = { promote: 'green', repeat: 'amber', leave: 'slate' } as const;
const ACTION_LABEL = { promote: 'Promoted', repeat: 'Repeating', leave: 'Left' } as const;

/** Promotion Report — Org tier. Reads promotion_log across schools in scope (see OrgPromotionService), the audit trail every promote() call now writes. */
export function OrgPromotionReportPage() {
  const [schoolId, setSchoolId] = useState('');
  const [academicYearName, setAcademicYearName] = useState('');
  const [className, setClassName] = useState('');
  const [exporting, setExporting] = useState(false);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-promotion-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const params = {
    schoolId: schoolId || undefined,
    academicYearName: academicYearName || undefined,
    className: className || undefined,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['org-promotion-log', params],
    queryFn: () => OrgPromotionApi.listLog(params),
  });

  const rows = data?.rows ?? [];

  const runExport = async () => {
    setExporting(true);
    try {
      await OrgPromotionApi.exportPdf(params);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Promotion Report"
        description="Audit trail of every promotion — single-school and bulk — across the schools in your organization."
        actions={
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            onClick={runExport}
            disabled={exporting}
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-slate-600" />}
            Export PDF
          </button>
        }
      />

      {data?.truncated && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          This report has more entries than can be shown at once — results are truncated. Narrow the filters to see more.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Input
          className="w-48"
          value={className}
          onChange={(e) => setClassName(e.target.value)}
          placeholder="Filter by class name"
        />
        <Input
          className="w-48"
          value={academicYearName}
          onChange={(e) => setAcademicYearName(e.target.value)}
          placeholder="Filter by academic year name"
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">School</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Admission #</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Student</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Action</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">From</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">To</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Actioned By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isFetching ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-400">
                  No promotion records found.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{r.createdAt?.slice(0, 10)}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-800">{r.schoolName}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">{r.admissionNumber}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-800">{r.studentName}</td>
                  <td className="px-4 py-2.5">
                    <Badge tone={ACTION_TONE[r.action]}>{ACTION_LABEL[r.action]}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">
                    {r.fromClassName} / {r.fromSectionName}
                    <div className="text-xs text-slate-400">{r.fromAcademicYearName}</div>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-600">
                    {r.toClassName ? (
                      <>
                        {r.toClassName} / {r.toSectionName}
                        <div className="text-xs text-slate-400">{r.toAcademicYearName}</div>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-500">{r.actorName ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
